import {ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, Renderer2, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormGroup, NgModel} from '@angular/forms';
import {DynamicIdService} from 'shared/_services/dynamic.id.service';
import {ControlTemplate} from '../_interface/control-template.enum';
import {NgbDate, NgbDateParserFormatter, NgbDateStruct, NgbInputDatepicker} from '@ng-bootstrap/ng-bootstrap';
import {ICalendarOverlay} from '../../../../journal/src/_interface/ICalendarOverlay';
import { set } from 'lodash-es';

const now = new Date();
const equals = (one: NgbDateStruct, two: NgbDateStruct) =>
	one && two && two.year === one.year && two.month === one.month && two.day === one.day;

const before = (one: NgbDateStruct, two: NgbDateStruct) =>
	!one || !two ? false : one.year === two.year ? one.month === two.month ? one.day === two.day
		? false : one.day < two.day : one.month < two.month : one.year < two.year;

const after = (one: NgbDateStruct, two: NgbDateStruct) =>
	!one || !two ? false : one.year === two.year ? one.month === two.month ? one.day === two.day
		? false : one.day > two.day : one.month > two.month : one.year > two.year;

@Component({
	selector: 'form-date-range-picker',
	templateUrl: './date-range-picker.component.html',
	styleUrls: ['./date-range-picker.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateRangePickerComponent implements OnInit {
	@ViewChild('dateRangePickerInput') input: NgbInputDatepicker;
	@ViewChild(NgModel) datePick: NgModel;
	@ViewChild('rangeInput') rangeInput: ElementRef;

	@Input()
	control: FormGroup;

	@Input()
	placeholder: string;

	@Input()
	id: string = this.dynamicIdService.getId('date-range-input');

	@Input()
	label: string;

	@Input()
	clearable = false;

	@Input()
	defaultValue: string;

	@Input()
	template: ControlTemplate = ControlTemplate.inline;

	@Input()
	startDate: NgbDateStruct;

	@Input()
	maxDate: NgbDateStruct;

	@Input()
	minDate: NgbDateStruct;

	@Input() set overlay(o: ICalendarOverlay[]) {
		o.forEach(item => set(this.calOverlay, `year_${item.dateObj.year}.month_${item.dateObj.month}.day_${item.dateObj.day}`, item));
	}

	ControlTemplate = ControlTemplate;
	hoveredDate: NgbDateStruct;
	fromDate: any;
	toDate: any;
	dateString = '';
	calOverlay: { [year: string]: { [month: string]: { [day: string]: ICalendarOverlay } } } = {};

	public get valueSet() {
		return Boolean(this.control?.controls?.start?.value);
	}

	constructor(
		private dynamicIdService: DynamicIdService,
		private renderer: Renderer2,
		private parserFormatter: NgbDateParserFormatter
	) {
	}

	ngOnInit(): void {
		if (!this.control) throw new Error('Control is required');

		const year = now.getFullYear();
		const month = now.getMonth();
		const day = now.getDate();

		if (!this.startDate) this.startDate = {year, month, day};
		if (!this.minDate) this.minDate = {year: year - 1, month, day};
		if (!this.maxDate) this.maxDate = {year, month: month + 1, day};

		if (this.control.value) {
			if (this.control.value.start && this.control.value.end) {
				this.dateString = `${this.parserFormatter.format(this.control.value.start)}   |   ${this.parserFormatter.format(this.control.value.end)}`;
			}
			if (this.control.value.start && (!this.control.value.end)) {
				this.dateString = `${this.parserFormatter.format(this.control.value.start)}`;
			}
			this.fromDate = this.control.value.start;
			this.toDate = this.control.value.end;
		}
	}

	clear = () => {
		this.control.controls.start.reset();
		this.control.controls.end.reset();
		this.control.markAsTouched();
		this.fromDate = null;
		this.toDate = null;
		this.dateString = '';
		this.rangeInput.nativeElement.value = '';
		this.startDate = {
			year: now.getFullYear(),
			month: now.getMonth(),
			day: now.getDate()
		};
	}

	isHovered = (date: NgbDateStruct) =>
		this.fromDate && !this.toDate && this.hoveredDate && after(date, this.fromDate) && before(date, this.hoveredDate)
	isInside = (date: NgbDateStruct) => after(date, this.fromDate) && before(date, this.toDate);
	isFrom = (date: NgbDateStruct) => equals(date, this.fromDate);
	isTo = (date: NgbDateStruct) => equals(date, this.toDate);

	onDateSelection(date: NgbDateStruct) {
		let parsed = '';
		if (!this.fromDate && !this.toDate) {
			this.fromDate = date;
		} else if (this.fromDate && !this.toDate && after(date, this.fromDate)) {
			this.toDate = date;
			this.input.close();
		} else {
			this.toDate = null;
			this.fromDate = date;
		}
		if (this.fromDate) {
			parsed += this.parserFormatter.format(this.fromDate);
			this.startDate = this.fromDate;
		}
		if (this.toDate) {
			parsed += '   |   ' + this.parserFormatter.format(this.toDate);
		}
		this.control.setValue({
			start: this.fromDate,
			end: this.toDate
		});

		this.renderer.setProperty(this.rangeInput.nativeElement, 'value', parsed);
		this.control.markAsTouched();
	}

	public isInRange(date: NgbDate): boolean {
		return this.isFrom(date) || this.isTo(date) || this.isInside(date) || this.isHovered(date);
	}

	public hasOverlay(date: NgbDate): boolean {
		return !!(
			this.calOverlay?.[`year_${date.year}`]?.[`month_${date.month}`]?.[`day_${date.day}`]
		);
	}

	public getEntriesLength(date: NgbDate): number {
		return this.calOverlay?.[`year_${date.year}`]?.[`month_${date.month}`]?.[`day_${date.day}`]?.entries?.length ?? 0;
	}

	public shouldShowMarker(date: NgbDate, threshold: number): boolean {
		const length = this.getEntriesLength(date);
		return threshold === 0 ? length > 0 : length > threshold;
	}
}
